import { Button, Stack } from '@mui/material'
import { curry, identity, ifElse, pick, pipe, propIs } from 'ramda'
import { useEffect } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { Case, Switch } from 'react-if'
import { useNavigate, useParams } from 'react-router-dom'

import { VailAccordion, VailSelect, VailTextField } from '@/components'
import LanguageSelector from '@/components/LanguageSelector'
import { useComponentProvider } from '@/components/ViewComponentModal/ViewComponentProvider'
import { useOrg, useUrlParams } from '@/hooks'
import WorkshopTitlePreview from '@/screens/ComponentLibrary/components/WorkshopTitlePreview'
import {
  useCreateCustomComponent,
  useGetCustomComponent,
  useUpdateCustomComponent,
} from '@/screens/ComponentLibrary/hooks'
import { safeCypressID } from '@/utils'
import { canUseName } from '@/utils'
import { ComponentType } from '@/utils/api'
import modifyPath from '@/utils/modifyPath'

import { localizedArray, SupportedLocales } from '../../types/customComponents'

export type AuthCodeType =
  | 'fullChallenge'
  | 'lastNameOnly'
  | 'noChallenge'
  | 'yearOfBirthOnly'

export type AuthCodeSettings = {
  type: AuthCodeType
  introText: localizedArray
  lastNamePrompt: localizedArray
  yearOfBirthPrompt: localizedArray
  yearOfBirthConstraintResults: localizedArray
  challengeRetryText: localizedArray
  challengeFailedText: localizedArray
  authCodeErrorInstructions: localizedArray
}

export type AuthCodeForm = {
  description: string
  name: string
  settings: AuthCodeSettings
}

export const Settings = () => {
  const { orgID } = useParams()
  const { componentID } = useComponentProvider()
  const org = useOrg(orgID)
  const [{ lang = 'en' }] = useUrlParams()
  const languagesSupported = org.data?.languagesSupported || ['en']

  const navigate = useNavigate()
  const current = useGetCustomComponent<AuthCodeSettings>(componentID)
  const create = useCreateCustomComponent<AuthCodeSettings>(orgID)
  const update = useUpdateCustomComponent<AuthCodeSettings>(componentID)
  const form = useForm<AuthCodeForm>({
    defaultValues: {
      settings: {
        authCodeErrorInstructions: {
          en: ['If this keeps happening, please contact your care team.'],
        },
        challengeFailedText: {
          en: [
            "I'm sorry, that's too many attempts. I can't allow you to log in from this phone number.",
          ],
        },
        challengeRetryText: {
          en: ['Your answer did not match our records. Please try again.'],
        },
        introText: {
          en: ['Before we continue, I need to confirm your identity.'],
        },
        lastNamePrompt: {
          en: ['What is your last name?'],
        },
        type: 'fullChallenge',
        yearOfBirthConstraintResults: {
          en: ['Please enter a valid birth year (yyyy).'],
        },
        yearOfBirthPrompt: { en: ['What is your year of birth?'] },
      },
    },
  })
  const { reset } = form
  useEffect(() => {
    if (!current.data) return
    const { name, description, settings } = current.data

    const localizeString = curry((str: string) =>
      ifElse(propIs(String, str), modifyPath([str], [str, 'en', 0]), identity)
    )
    const updateSettings = pipe<
      [AuthCodeSettings],
      AuthCodeSettings,
      AuthCodeSettings,
      AuthCodeSettings,
      AuthCodeSettings,
      AuthCodeSettings,
      AuthCodeSettings,
      AuthCodeSettings
    >(
      localizeString('introText'),
      localizeString('lastNamePrompt'),
      localizeString('yearOfBirthPrompt'),
      localizeString('yearOfBirthConstraintResults'),
      localizeString('challengeRetryText'),
      localizeString('challengeFailedText'),
      localizeString('authCodeErrorInstructions')
    )

    console.log(settings, updateSettings(settings))
    reset({
      description,
      name,
      settings: updateSettings(settings),
    })
  }, [current.data, reset])

  const onSubmit = form.handleSubmit(async (data) => {
    const dataToSave = pick(['name', 'description', 'settings'], data)

    if (componentID)
      return await update.mutateAsync({
        ...dataToSave,
        id: componentID,
      })
    const res = await create.mutateAsync({
      ...dataToSave,
      type: ComponentType.authCode,
    })
    return navigate(`../${res?.id}`)
  })

  const authTypeOptions: { label: string; value: AuthCodeType }[] = [
    { label: 'Full Challenge', value: 'fullChallenge' },
    { label: 'Last Name Only', value: 'lastNameOnly' },
    { label: 'No Challenge', value: 'noChallenge' },
    { label: 'Year of Birth Only', value: 'yearOfBirthOnly' },
  ]

  const authType = form.watch('settings.type')
  const isOfType = (type: AuthCodeType[]) => type.includes(authType)

  return (
    <FormProvider {...form}>
      <Stack
        component="form"
        height="100%"
        justifyContent="start"
        rowGap={2}
        onSubmit={onSubmit}
      >
        <Button
          id={safeCypressID('save auth code')}
          sx={{ alignSelf: 'flex-end', mb: 2 }}
          type="submit"
        >
          Save
        </Button>
        <VailAccordion
          defaultExpanded
          useDefaultBox
          detailsSx={{
            display: 'flex',
            flexDirection: ['column', null, 'row'],
            gap: 2,
          }}
          title="Description"
        >
          <Stack flex={1} gap={2}>
            <VailTextField
              control={form.control}
              label="Name"
              name="name"
              rules={{
                validate: {
                  canUseName: async (data) =>
                    canUseName(orgID, componentID, data),
                },
              }}
            />
            <WorkshopTitlePreview name={form.watch('name')} />
            <VailSelect
              disabled
              defaultValue={ComponentType.authCode}
              label="Type"
              name="type"
              options={[{ label: 'Auth Code', value: ComponentType.authCode }]}
            />
          </Stack>
          <Stack flex={1}>
            <VailTextField
              multiline
              control={form.control}
              label="Description"
              maxRows={5}
              minRows={5}
              name="description"
              rules={{ required: false }}
            />
          </Stack>
        </VailAccordion>
        <VailAccordion
          defaultExpanded
          useDefaultBox
          detailsSx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
          title="Configuration"
        >
          <LanguageSelector />
          <VailSelect
            control={form.control}
            defaultValue="fullChallenge"
            label="Type"
            name="settings.type"
            options={authTypeOptions}
          />
          <Switch>
            {languagesSupported.map((orgLang) => (
              <Case key={orgLang} condition={orgLang === lang}>
                <VailTextField
                  control={form.control}
                  label="Intro Text"
                  name={`settings.introText.${orgLang as SupportedLocales}.0`}
                  rules={{}}
                />
              </Case>
            ))}
          </Switch>

          {isOfType(['fullChallenge', 'lastNameOnly']) && (
            <Switch>
              {languagesSupported.map((orgLang) => (
                <Case key={orgLang} condition={orgLang === lang}>
                  <VailTextField
                    control={form.control}
                    label="Last Name Prompt"
                    name={`settings.lastNamePrompt.${
                      orgLang as SupportedLocales
                    }.0`}
                  />
                </Case>
              ))}
            </Switch>
          )}
          {isOfType(['fullChallenge', 'yearOfBirthOnly']) && (
            <Switch>
              {languagesSupported.map((orgLang) => (
                <Case key={orgLang} condition={orgLang === lang}>
                  <VailTextField
                    control={form.control}
                    label="Year of Birth Prompt"
                    name={`settings.yearOfBirthPrompt.${
                      orgLang as SupportedLocales
                    }.0`}
                  />
                </Case>
              ))}
            </Switch>
          )}
          {isOfType(['fullChallenge', 'yearOfBirthOnly']) && (
            <Switch>
              {languagesSupported.map((orgLang) => (
                <Case key={orgLang} condition={orgLang === lang}>
                  <VailTextField
                    control={form.control}
                    label="Year Validation"
                    name={`settings.yearOfBirthConstraintResults.${
                      orgLang as SupportedLocales
                    }.0`}
                  />
                </Case>
              ))}
            </Switch>
          )}
          {isOfType(['fullChallenge', 'yearOfBirthOnly', 'lastNameOnly']) && (
            <Switch>
              {languagesSupported.map((orgLang) => (
                <Case key={orgLang} condition={orgLang === lang}>
                  <VailTextField
                    control={form.control}
                    label="Retry Challenge"
                    name={`settings.challengeRetryText.${
                      orgLang as SupportedLocales
                    }.0`}
                  />
                </Case>
              ))}
            </Switch>
          )}
          {isOfType(['fullChallenge', 'yearOfBirthOnly', 'lastNameOnly']) && (
            <Switch>
              {languagesSupported.map((orgLang) => (
                <Case key={orgLang} condition={orgLang === lang}>
                  <VailTextField
                    control={form.control}
                    label="Failed Challenge"
                    name={`settings.challengeFailedText.${
                      orgLang as SupportedLocales
                    }.0`}
                  />
                </Case>
              ))}
            </Switch>
          )}
          <Switch>
            {languagesSupported.map((orgLang) => (
              <Case key={orgLang} condition={orgLang === lang}>
                <VailTextField
                  control={form.control}
                  label="Auth Code Error"
                  name={`settings.authCodeErrorInstructions.${
                    orgLang as SupportedLocales
                  }.0`}
                />
              </Case>
            ))}
          </Switch>
        </VailAccordion>
      </Stack>
    </FormProvider>
  )
}
